/* eslint-disable camelcase */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { nanoid } from 'nanoid';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Image from 'next/image';
import useSWR from 'swr';
import Select from 'react-select';
import { formatRelative, compareDesc, compareAsc, format } from 'date-fns';
import es from 'date-fns/locale/es';
// import { getSession, useSession } from 'next-auth/client';
import { useSession } from 'next-auth/client';
import { setLocalSession, localLogout } from '@/utils/auth';
import { ViewWrapper, DashboardPageWrapper } from '@/styles/index';
import { Icon } from 'react-icons-kit';
import { ic_add } from 'react-icons-kit/md/ic_add';
import { ic_mode_edit } from 'react-icons-kit/md/ic_mode_edit';
import { ic_more_vert } from 'react-icons-kit/md/ic_more_vert';
import { trashO } from 'react-icons-kit/fa/trashO';
import { settings } from 'react-icons-kit/feather/settings';
import { ic_chevron_right } from 'react-icons-kit/md/ic_chevron_right';
import { ic_info_outline } from 'react-icons-kit/md/ic_info_outline';
import { thumbTack } from 'react-icons-kit/fa/thumbTack';
import { ic_update } from 'react-icons-kit/md/ic_update';
import { caretDown } from 'react-icons-kit/fa/caretDown';
import { caretUp } from 'react-icons-kit/fa/caretUp';
import { sortAlphaAsc } from 'react-icons-kit/fa/sortAlphaAsc';
import { sortAlphaDesc } from 'react-icons-kit/fa/sortAlphaDesc';
import useIntlMessage from '@/hooks/use-intl-message';
import { axiosFetcher, updateUser, deletePoll, updatePoll } from '@/utils/data';
// import Tippy from '@/components/Tooltip';
import UserNavbar from '@/components/Layout/Navbar/UserNavbar';
// import Footer from '@/components/Layout/Footer';
import HeadMeta from '@/components/HeadMeta';
import SplashLoader from '@/components/SplashLoader';

const Footer = dynamic(() => import('@/components/Layout/Footer'));
const Tippy = dynamic(() => import('@/components/Tooltip'), { ssr: false });

const dateLocales = {
  es
};

// export async function getServerSideProps(context) {
//   // console.log('server session');
//   const session = await getSession(context);
//   // console.log({ session });
//   return {
//     props: { session }
//   };
// }

const EditFolderTooltip = ({ folders, folderId, loading, onEditFolder }) => {
  const { intlMessage: m } = useIntlMessage();
  const [folderName, setFolderName] = useState('');
  const currFolder = folders.find(fol => fol.id === folderId);

  useEffect(() => {
    if (currFolder) {
      setFolderName(currFolder.name);
    }
  }, [currFolder]);

  const handleEditFolderName = () => {
    onEditFolder({ ...currFolder, name: folderName });
  };

  return (
    <div className="update-folder-tooltip">
      <div className="tooltip-label">{m({ id: 'edit_folder', route: 'pages.dashboard' })}</div>
      <div className="tooltip-input-wrappper">
        <input
          value={folderName}
          disabled={loading}
          onChange={e => setFolderName(e.target.value)}
          placeholder="Folder name..."
        />
      </div>
      <div className="tooltip-footer">
        <button type="button" disabled={loading} onClick={handleEditFolderName}>
          {m({ id: 'update_folder', route: 'pages.dashboard' })}{' '}
          <Icon size={14} icon={ic_mode_edit} />
        </button>
      </div>
    </div>
  );
};

const RemoveFolderTooltip = ({ folders, folderId, loading, onDeleteFolder }) => {
  const { intlMessage: m } = useIntlMessage();
  const currFolder = folders.find(fol => fol.id === folderId);

  const handleDeleteFolderId = () => {
    onDeleteFolder(folderId);
  };

  return (
    <div className="remove-folder-tooltip">
      <div className="tooltip-label">{m({ id: 'remove_folder', route: 'pages.dashboard' })}</div>
      <div>
        {m({ id: 'are_your_sure', route: 'pages.dashboard' })} <em>{currFolder.name}</em>{' '}
        {m({ id: 'folder', route: 'pages.dashboard' })}?
      </div>
      <div className="tooltip-footer">
        <button
          type="button"
          className="remove-folder-confirm"
          disabled={loading}
          onClick={handleDeleteFolderId}
        >
          {m({ id: 'remove_folder', route: 'pages.dashboard' })} <Icon size={14} icon={trashO} />
        </button>
      </div>
    </div>
  );
};

const Dashboard = ({ session }) => {
  // console.log({ session });
  const router = useRouter();
  const { locale, defaultLocale } = router;
  // console.log({ locale, defaultLocale });
  const [polls, setPolls] = useState([]);
  const [folderFilter, setFolderFilter] = useState('All');
  const [folders, setFolders] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [sorting, setSorting] = useState('updated-desc');
  const [folderOptions, setFolderOptions] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [newFolder, setNewFolder] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFolderTooltip, setShowFolderTooltip] = useState(false);
  const [showEditFolderTooltip, setShowEditFolderTooltip] = useState(false);
  const [showDeleteTooltip, setShowDeleteTooltip] = useState('');
  const [showAddToFolderTooltip, setShowAddToFolderTooltip] = useState('');
  const { intlMessage: m } = useIntlMessage();
  // https://swr.vercel.app/docs/arguments
  const { data, error, mutate } = useSWR(
    [`/api/poll/list`],
    url => axiosFetcher(url)
    // { initialData: poll } // https://swr.vercel.app/docs/with-nextjs
    // doesn't work on this case, as we need an initial call to check if owner or not
  );

  const allPolls = data?.polls || [];
  const allPinnedPolls = pinned.map(pinId => allPolls.find(poll => poll.id === pinId));
  const getUncategorizedPolls = pollList =>
    pollList.filter(pollItem => {
      const pollFolder = pollItem.folder;
      const userFolderIds = folders.map(fol => fol.id);
      const folderExist = pollFolder && userFolderIds.includes(pollFolder);
      const validPollFolder = pollFolder && folderExist;
      return !validPollFolder; // return filter of not valid poll folders
    });
  const uncategorizedPolls = getUncategorizedPolls(allPolls);
  const currentUserFolderFilter = folders.find(fol => fol.id === folderFilter);
  const parsedFolderName = currentUserFolderFilter
    ? currentUserFolderFilter.name
    : `${m({ id: 'uncategorized', route: 'pages.dashboard' })}`;
  const folderName = folderFilter
    ? parsedFolderName
    : `${m({ id: 'all', route: 'pages.dashboard' })}`;

  const sortPolls = (newPolls, sortDirection) => {
    const pollsArray = newPolls || polls;
    const pollsArrClone = [...pollsArray];
    let sortedPolls = [];
    const updatedDesc = arrToSort =>
      arrToSort.sort((a, b) => {
        const dateLeft = new Date(a.modifiedAt || a.updatedAt);
        const dateRight = new Date(b.modifiedAt || b.updatedAt);
        return compareDesc(dateLeft, dateRight);
      });
    const updatedAsc = arrToSort =>
      arrToSort.sort((a, b) => {
        const dateLeft = new Date(a.modifiedAt || a.updatedAt);
        const dateRight = new Date(b.modifiedAt || b.updatedAt);
        return compareAsc(dateLeft, dateRight);
      });
    const updatedTitleDesc = arrToSort => arrToSort.sort((a, b) => b.title.localeCompare(a.title));
    const updatedTitleAsc = arrToSort => arrToSort.sort((a, b) => a.title.localeCompare(b.title));
    switch (sortDirection) {
      case 'updated-desc':
        sortedPolls = updatedDesc(pollsArrClone);
        break;
      case 'updated-asc':
        sortedPolls = updatedAsc(pollsArrClone);
        break;
      case 'alpha-desc':
        sortedPolls = updatedTitleDesc(pollsArrClone);
        break;
      case 'alpha-asc':
        sortedPolls = updatedTitleAsc(pollsArrClone);
        break;
      default:
        sortedPolls = updatedDesc(pollsArrClone);
        break;
    }
    return sortedPolls;
  };

  useEffect(() => {
    async function updateSortingOrder(newSort) {
      // console.log('sorting:', newSort);
      const sortedPollList = sortPolls(null, newSort);
      // console.log({ sortedPollList });
      await setPolls(sortedPollList);
    }
    updateSortingOrder(sorting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting]);

  const filterPolls = (folderId, newPolls) => {
    const currentFolderFilter = folderId || folderFilter;
    const currentAllPolls = newPolls || allPolls;
    const currentUncatPolls = getUncategorizedPolls(currentAllPolls);
    let filteredPolls = [];
    switch (currentFolderFilter) {
      case '':
        filteredPolls = sortPolls(currentAllPolls, sorting);
        break;
      case 'All':
        filteredPolls = sortPolls(currentAllPolls, sorting);
        break;
      case 'Uncategorized':
        filteredPolls = sortPolls(currentUncatPolls, sorting);
        break;
      default:
        filteredPolls = sortPolls(
          currentAllPolls.filter(pollItem => pollItem.folder === currentFolderFilter),
          sorting
        );
        break;
    }
    return filteredPolls;
  };

  useEffect(() => {
    async function getData(newData) {
      if (newData) {
        const currPolls = newData?.polls || [];
        if (currPolls.length > 0) {
          // check if filter present
          const filteredPolls = filterPolls(null, currPolls);
          await setPolls(filteredPolls);
          // const sortedPolls = sortPolls(currPolls);
          // setPolls(sortedPolls);
        } else {
          await setPolls(currPolls);
        }
        const dataFolders = newData.user?.folders;
        const dataPinned = newData.user?.pinnedPolls;
        const userFolders = dataFolders ? JSON.parse(dataFolders) : [];
        const userPinnedPolls = dataPinned ? JSON.parse(dataPinned) : [];
        await setFolders(userFolders);
        await setPinned(userPinnedPolls);
        const folderSelectOptions = userFolders.map(fol => ({
          value: fol.id,
          label: fol.name
        }));
        await setFolderOptions([{ value: null, label: 'No folder' }, ...folderSelectOptions]);
      }
    }
    getData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // console.log({ data });

  // https://developer.mozilla.org/en-US/docs/Web/API/Document/drag_event
  const holdPollRef = useRef();
  const handleDragEnd = e => {
    e.target.style.opacity = '';
  };
  const handleDragEnter = e => {
    const folderId = e.target.getAttribute('data-folder');
    if (folderId) {
      // e.target.style.background = 'purple';
      e.target.style.border = '2px dashed var(--muted-strong)';
    }
  };
  const handleDragLeave = e => {
    const folderId = e.target.getAttribute('data-folder');
    if (folderId) {
      // e.target.style.background = '';
      e.target.style.border = '';
    }
  };
  const handleDragDropFn = async e => {
    // console.log('on drag DROP');
    // console.log(e.target);
    e.preventDefault();
    // console.log('check existing Poll ref');
    // console.log(holdPollRef);
    const folderId = e.target.getAttribute('data-folder');
    // console.log({ folderId });
    // if no holded poll
    if (!holdPollRef.current) {
      return null;
    }
    // if no folder reference
    if (!folderId) {
      holdPollRef.current = undefined;
      return null;
    }
    // change target feedback
    e.target.style.border = '';
    e.target.className = 'folder-item loading';
    // update poll folder!
    const pollRef = holdPollRef.current;
    await setLoading(true);
    const pollInput = { id: pollRef.id, folder: folderId };
    const localPollToUpdate = polls.find(p => p.id === pollRef.id);
    const localPollIndex = polls.indexOf(localPollToUpdate);
    const optimisticPolls = [...polls];
    optimisticPolls[localPollIndex] = {
      ...localPollToUpdate,
      folder: folderId
    };
    // remove poll ref
    holdPollRef.current = undefined;
    // console.log({ pollInput });
    await updatePoll({
      poll: pollInput,
      accessKey: localPollToUpdate.accessKey
    });
    // update data
    await mutate({ ...data, polls: optimisticPolls }); // update local data immediately and revalidate SWR https://swr.vercel.app/docs/mutation#bound-mutate
    await setShowFolderTooltip(false);
    await setShowDeleteTooltip('');
    await setShowAddToFolderTooltip('');
    await setSelectedFolder(null);
    await setLoading(false);
    // clean target border feedback
    e.target.style.border = '';
    e.target.className = 'folder-item';
  };

  const handleDragDrop = useCallback(handleDragDropFn, [data, mutate, polls]);

  const handleDragOver = e => {
    e.preventDefault();
  };

  useEffect(() => {
    // console.log('RENDERING LISTENERS');
    const handleDragDown = e => {
      // console.log('on drag down');
      // console.log(e.target);
      const targetId = e.target.getAttribute('data-id');
      // console.log({ targetId });
      if (!targetId) {
        e.preventDefault();
        holdPollRef.current = undefined;
        return null;
      }
      // if loading state, return
      if (loading) {
        e.preventDefault();
        holdPollRef.current = undefined;
        return null;
      }
      // e.preventDefault();
      e.target.style.opacity = 0.5;
      const selectedPoll = polls.find(poll => poll.slug === targetId);
      // console.log({ selectedPoll });
      // set poll to ref
      holdPollRef.current = selectedPoll;
    };
    // add when mounted
    document.addEventListener('dragstart', handleDragDown);
    document.addEventListener('dragend', handleDragEnd);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDragDrop);
    document.addEventListener('dragover', handleDragOver);

    return () => {
      document.removeEventListener('dragstart', handleDragDown);
      document.removeEventListener('dragend', handleDragEnd);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDragDrop);
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [handleDragDrop, loading, polls]);

  if (!session) {
    return <div>No session</div>;
  }

  const handleFolderFilter = async folderId => {
    // const allPolls = data?.polls|| [];
    const filteredPolls = filterPolls(folderId);
    await setPolls(filteredPolls);
    await setFolderFilter(folderId);
    // const currUserFolder = folders.find(fol => fol.id === folderId);
  };

  const handleDeletePoll = async ({ id, accessKey }) => {
    await setLoading(true);
    await deletePoll({ id, accessKey });
    await mutate(); // revalidate SWR
    await setShowFolderTooltip(false);
    await setShowDeleteTooltip('');
    await setLoading(false);
  };

  const handleUpdatePollFolder = async ({ id, accessKey }) => {
    await setLoading(true);
    // console.log({ selectedFolder });
    const pollInput = { id, folder: selectedFolder.value };
    // console.log({ pollInput });
    await updatePoll({ poll: pollInput, accessKey });
    await mutate(); // revalidate SWR
    await setShowFolderTooltip(false);
    await setShowDeleteTooltip('');
    await setShowAddToFolderTooltip('');
    await setLoading(false);
    await setSelectedFolder(null);
  };

  // console.log({ data, error, mutate });
  // console.log({ polls });

  const onCreateFolder = async () => {
    await setLoading(true);
    const newFoldersValue = [...folders, { id: nanoid(6), name: newFolder }];
    await updateUser({ folders: JSON.stringify(newFoldersValue) });
    await mutate(); // revalidate SWR
    await setShowFolderTooltip(false);
    await setNewFolder('');
    await setLoading(false);
  };
  const onDeleteFolder = async folderId => {
    await setLoading(true);
    const newFoldersValue = [...folders.filter(folder => folder.id !== folderId)];
    await updateUser({ folders: JSON.stringify(newFoldersValue) });
    await mutate(); // revalidate SWR
    await setShowFolderTooltip(false);
    await setNewFolder('');
    await setLoading(false);
    handleFolderFilter('All');
  };

  const pinNewPollId = async pollId => {
    await setLoading(true);
    const newPinnedValue = [...pinned, pollId];
    const optimisticUser = {
      ...data.user,
      pinnedPolls: JSON.stringify(newPinnedValue)
    };
    await updateUser({ pinnedPolls: JSON.stringify(newPinnedValue) });
    await mutate({ ...data, user: optimisticUser }); // revalidate SWR
    await setLoading(false);
  };
  const unPinPollId = async pollId => {
    await setLoading(true);
    const newPinnedValue = [...pinned.filter(pinnedId => pinnedId !== pollId)];
    const optimisticUser = {
      ...data.user,
      pinnedPolls: JSON.stringify(newPinnedValue)
    };
    await updateUser({ pinnedPolls: JSON.stringify(newPinnedValue) });
    await mutate({ ...data, user: optimisticUser }); // revalidate SWR
    await setLoading(false);
  };

  const onEditFolder = async folderObject => {
    const newFoldersValue = [...folders];
    const folderToEdit = newFoldersValue.find(fol => fol.id === folderObject.id);
    const indexOfFolder = newFoldersValue.indexOf(folderToEdit);
    newFoldersValue[indexOfFolder] = folderObject;
    await setLoading(true);
    await updateUser({ folders: JSON.stringify(newFoldersValue) });
    await mutate(); // revalidate SWR
    await setShowFolderTooltip(false);
    await setNewFolder('');
    await setLoading(false);
    await setShowEditFolderTooltip(false);
  };

  const NewFolderTooltip = (
    <div className="new-folder-tooltip">
      <div className="tooltip-label">{m({ id: 'new_folder', route: 'pages.dashboard' })}</div>
      <div className="tooltip-input-wrappper">
        <input
          value={newFolder}
          disabled={loading}
          onChange={e => setNewFolder(e.target.value)}
          placeholder="Folder name..."
        />
      </div>
      <div className="tooltip-footer">
        <button type="button" disabled={loading} onClick={onCreateFolder}>
          {m({ id: 'add_folder', route: 'pages.dashboard' })} <Icon size={14} icon={ic_add} />
        </button>
      </div>
    </div>
  );

  const isENLocale = locale === 'en';
  const localeOptions = isENLocale ? {} : { locale: dateLocales[`${locale}`] };

  return (
    <ViewWrapper>
      <HeadMeta
        title={
          session.user.name
            ? `Dashboard - ${session.user.name}, Pollme`
            : `Dashboard - ${session.username}, Pollme`
        }
      />
      <DashboardPageWrapper>
        <UserNavbar session={session} user={data?.user} />
        <div className="inner-dashboard">
          <div className="side-nav">
            <div className="folder-nav">
              <div className="menu-header">
                <Tippy
                  theme="light"
                  placement="right-start"
                  visible={showFolderTooltip}
                  onClickOutside={() => setShowFolderTooltip(false)}
                  interactive
                  maxWidth={250}
                  // offset={[0, 1]}
                  // arrow={false}
                  content={NewFolderTooltip}
                >
                  <button type="button" onClick={() => setShowFolderTooltip(!showFolderTooltip)}>
                    <Icon size={14} icon={ic_add} />{' '}
                    {m({ id: 'new_folder', route: 'pages.dashboard' })}
                  </button>
                </Tippy>
              </div>
              <div className="menu-list">
                <button
                  className="folder-item"
                  type="button"
                  onClick={() => handleFolderFilter('All')}
                  data-selected={folderFilter === 'All'}
                >
                  <div className="folder-title">{m({ id: 'all', route: 'pages.dashboard' })}</div>
                  <div className="items-count">{allPolls.length}</div>
                </button>
                <button
                  className="folder-item"
                  key="Uncategorized"
                  type="button"
                  onClick={() => handleFolderFilter('Uncategorized')}
                  data-selected={folderFilter === 'Uncategorized'}
                >
                  <div className="folder-title">
                    {m({ id: 'uncategorized', route: 'pages.dashboard' })}
                  </div>
                  <div className="items-count">{uncategorizedPolls.length}</div>
                </button>
                {folders.map(folder => (
                  <button
                    className="folder-item"
                    key={folder.id}
                    data-folder={folder.id}
                    type="button"
                    onClick={() => handleFolderFilter(folder.id)}
                    data-selected={folderFilter === folder.id}
                  >
                    <div className="folder-title">{folder.name}</div>
                    <div className="items-count">
                      {allPolls.filter(pollItem => pollItem.folder === folder.id).length}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="dash-content">
            <div className="inner-nav">
              <div className="filters">
                <Tippy content="Sort Updated Desc.">
                  <button
                    type="button"
                    className={sorting === 'updated-desc' ? 'active' : ''}
                    onClick={() => setSorting('updated-desc')}
                  >
                    <Icon icon={ic_update} /> <Icon icon={caretDown} />
                  </button>
                </Tippy>

                <Tippy content="Sort Updated Asc.">
                  <button
                    type="button"
                    className={sorting === 'updated-asc' ? 'active' : ''}
                    onClick={() => setSorting('updated-asc')}
                  >
                    <Icon icon={ic_update} /> <Icon icon={caretUp} />
                  </button>
                </Tippy>

                <Tippy content="Sort Alphabetically Asc.">
                  <button
                    type="button"
                    className={sorting === 'alpha-asc' ? 'active' : ''}
                    onClick={() => setSorting('alpha-asc')}
                  >
                    <Icon icon={sortAlphaAsc} />
                  </button>
                </Tippy>

                <Tippy content="Sort Alphabetically Desc.">
                  <button
                    type="button"
                    className={sorting === 'alpha-desc' ? 'active' : ''}
                    onClick={() => setSorting('alpha-desc')}
                  >
                    <Icon icon={sortAlphaDesc} />
                  </button>
                </Tippy>
              </div>
              <div className="actions">
                <Link href="/create">
                  <a>
                    <button type="button">
                      <Icon size={18} icon={ic_add} />{' '}
                      {m({ id: 'create_new_poll', route: 'pages.dashboard' })}
                    </button>
                  </a>
                </Link>
              </div>
            </div>
            {pinned.length > 0 && (
              <div className="poll-list">
                <div className="list-heading">
                  {m({ id: 'pinned_polls', route: 'pages.dashboard' })}{' '}
                  <Icon size={16} icon={thumbTack} />
                </div>
                <div className="list-container">
                  {allPinnedPolls.map(poll => {
                    const statusLabel = `${poll.status.charAt(0).toUpperCase()}${poll.status.slice(
                      1
                    )}`;
                    const responsesLenght = poll.responses.items.length;
                    return (
                      <div key={poll.id} className="pinned-poll-item">
                        <div className="poll-details">
                          {poll.folder && folders.find(fol => fol.id === poll.folder) && (
                            <div className="poll-folder-tag">
                              <button
                                type="button"
                                className="tag"
                                onClick={() => handleFolderFilter(poll.folder)}
                              >
                                {folders.find(fol => fol.id === poll.folder)?.name}
                              </button>
                            </div>
                          )}
                          <Link href={`/p/${poll.slug}`} data-id={poll.slug}>
                            <a>
                              <div className="poll-title" data-id={poll.slug}>
                                {poll.title}
                              </div>
                            </a>
                          </Link>
                        </div>
                        <div className="pinned-stats">
                          <div className="stat-block">
                            <div className="stat-label">{statusLabel}</div>
                            <div className="status-info">
                              {m({ id: 'status', route: 'pages.dashboard' })}
                            </div>
                          </div>
                          <div className="stat-block">
                            <div className="stat-label">{responsesLenght}</div>
                            <div className="status-info">
                              {m({ id: 'responses', route: 'pages.dashboard' })}
                            </div>
                          </div>
                          <div className="stat-block">
                            <div className="stat-label">
                              <Link href={`/p/${poll.slug}`}>
                                <a>
                                  {m({
                                    id: 'details',
                                    route: 'pages.dashboard'
                                  })}{' '}
                                  <Icon size={20} icon={ic_chevron_right} />
                                </a>
                              </Link>{' '}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="poll-list">
              <div className="list-heading">
                {m({ id: 'showing', route: 'pages.dashboard' })}:{' '}
                {folderFilter === 'All'
                  ? `${m({ id: 'all', route: 'pages.dashboard' })}`
                  : folderName}{' '}
                {m({ id: 'polls', route: 'pages.dashboard' })}{' '}
                {currentUserFolderFilter && (
                  <div className="folder-edit">
                    <Tippy
                      theme="light"
                      placement="right"
                      visible={showEditFolderTooltip}
                      onClickOutside={() => setShowEditFolderTooltip(false)}
                      interactive
                      maxWidth={250}
                      // trigger="click"
                      // offset={[0, 1]}
                      // arrow={false}
                      content={
                        <EditFolderTooltip
                          folders={folders}
                          folderId={folderFilter}
                          loading={loading}
                          onEditFolder={onEditFolder}
                        />
                      }
                    >
                      <button
                        type="button"
                        onClick={() => setShowEditFolderTooltip(!showEditFolderTooltip)}
                      >
                        <Icon size={14} icon={ic_mode_edit} />
                      </button>
                    </Tippy>
                  </div>
                )}
                {currentUserFolderFilter && (
                  <div className="folder-remove">
                    <Tippy
                      theme="light"
                      placement="right"
                      trigger="click"
                      // visible={showEditFolderTooltip}
                      // onClickOutside={() => setShowEditFolderTooltip(false)}
                      interactive
                      maxWidth={250}
                      // trigger="click"
                      // offset={[0, 1]}
                      // arrow={false}
                      content={
                        <RemoveFolderTooltip
                          folders={folders}
                          folderId={folderFilter}
                          loading={loading}
                          onDeleteFolder={onDeleteFolder}
                        />
                      }
                    >
                      <button
                        type="button"
                        className="remove-btn"
                        // onClick={() => setShowEditFolderTooltip(!showEditFolderTooltip)}
                      >
                        <Icon size={14} icon={trashO} />
                      </button>
                    </Tippy>
                  </div>
                )}
              </div>
              <div className="list-container">
                {polls.map(poll => {
                  // const statusLabel = poll.status;
                  const statusLabel = `${poll.status.charAt(0).toUpperCase()}${poll.status.slice(
                    1
                  )}`;
                  // console.log({ poll });
                  const pollModel = poll.model ? JSON.parse(poll.model) : {};
                  // console.log({ pollModel });
                  const questionsLenght = pollModel.pages.map(page => page.elements).flat().length;
                  // console.log({ questionsLenght });
                  const responsesLenght = poll.responses.items.length;
                  const responseDates = poll.responses.items
                    .map(resp => new Date(resp.updatedAt))
                    .sort(compareDesc);
                  // console.log({ responseDates });
                  const lastResponse = responseDates[0];
                  const isPinned = pinned.includes(poll.id);

                  return (
                    <div className="poll-item" key={poll.id} data-id={poll.slug} draggable="true">
                      {isPinned && (
                        <div className="poll-pinned-icon">
                          <Tippy content="Pinned">
                            <div>
                              <Icon size={14} icon={thumbTack} />
                            </div>
                          </Tippy>
                        </div>
                      )}
                      <div className="poll-details">
                        {poll.folder && folders.find(fol => fol.id === poll.folder) && (
                          <div className="poll-folder-tag">
                            <button
                              type="button"
                              className="tag"
                              onClick={() => handleFolderFilter(poll.folder)}
                            >
                              {folders.find(fol => fol.id === poll.folder)?.name}
                            </button>
                          </div>
                        )}
                        <Link href={`/p/${poll.slug}`} data-id={poll.slug}>
                          <a>
                            <div className="poll-title" data-id={poll.slug}>
                              {poll.title}
                            </div>
                          </a>
                        </Link>
                        <div className="status-info">
                          {m({ id: 'last_modified', route: 'pages.dashboard' })}:{' '}
                          {formatRelative(new Date(poll.modifiedAt || poll.updatedAt), new Date(), {
                            ...localeOptions
                          })}
                        </div>
                      </div>
                      <div className="poll-stats">
                        <div className="stat-block">
                          <div className="stat-label">{statusLabel}</div>
                          <div className="status-info">
                            {m({ id: 'status', route: 'pages.dashboard' })}
                          </div>
                        </div>
                        <div className="stat-block">
                          <div className="stat-label">{questionsLenght}</div>
                          <div className="status-info">
                            {m({
                              id: 'questions',
                              route: 'pages.dashboard'
                            })}
                          </div>
                        </div>
                        <div className="stat-block">
                          <div className="stat-label">{responsesLenght}</div>
                          <div className="status-info">
                            {m({ id: 'responses', route: 'pages.dashboard' })}
                          </div>
                        </div>
                        <div className="stat-block last-response">
                          <div className="stat-label">
                            {lastResponse
                              ? format(new Date(lastResponse), 'EEEEEE. do, MMM', {
                                  ...localeOptions
                                })
                              : `${m({
                                  id: 'never',
                                  route: 'pages.dashboard'
                                })}`}
                          </div>
                          <div className="status-info">
                            {m({
                              id: 'last_response',
                              route: 'pages.dashboard'
                            })}
                          </div>
                        </div>
                        <div className="stat-block">
                          <div className="poll-options">
                            <Tippy
                              theme="light"
                              placement="left"
                              trigger="click"
                              interactive
                              content={
                                <div className="poll-item-tooltip">
                                  <button
                                    type="button"
                                    disabled={loading}
                                    className={isPinned ? 'pinned-active' : ''}
                                    onClick={() =>
                                      isPinned ? unPinPollId(poll.id) : pinNewPollId(poll.id)
                                    }
                                  >
                                    {isPinned
                                      ? `${m({
                                          id: 'pinned',
                                          route: 'pages.dashboard'
                                        })}`
                                      : `${m({
                                          id: 'pin',
                                          route: 'pages.dashboard'
                                        })} Poll`}{' '}
                                    <Icon size={16} icon={thumbTack} />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() => router.push(`/p/${poll.slug}`)}
                                  >
                                    {m({
                                      id: 'details',
                                      route: 'pages.dashboard'
                                    })}{' '}
                                    <Icon size={16} icon={ic_chevron_right} />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={loading}
                                    onClick={() =>
                                      router.push({
                                        pathname: `/p/${poll.slug}`,
                                        query: { tab: 'settings' }
                                      })
                                    }
                                  >
                                    {m({
                                      id: 'settings',
                                      route: 'pages.dashboard'
                                    })}{' '}
                                    <Icon size={16} icon={settings} />
                                  </button>
                                  <Tippy
                                    theme="light"
                                    // placement="left-end"
                                    // trigger="click"
                                    visible={showAddToFolderTooltip === poll.id}
                                    onClickOutside={() => setShowAddToFolderTooltip('')}
                                    interactive
                                    content={
                                      <div className="poll-remove-tooltip">
                                        <div className="tooltip-label">
                                          <Icon size={16} icon={ic_add} />{' '}
                                          {poll.folder ? 'Change folder' : 'Add to folder'}
                                        </div>
                                        <div className="tooltip-info-text">
                                          {m({
                                            id: 'add_your',
                                            route: 'pages.dashboard'
                                          })}{' '}
                                          "<strong>{poll.title}</strong>" poll{' '}
                                          {m({
                                            id: 'to_custom_folder',
                                            route: 'pages.dashboard'
                                          })}
                                          :
                                        </div>
                                        <div className="lang-select">
                                          <Select
                                            id="poll-lang-card"
                                            instanceId="poll-lang-card"
                                            className="card-lang-dropdown"
                                            classNamePrefix="card-lang-dropdown"
                                            options={folderOptions}
                                            isDisabled={loading}
                                            value={selectedFolder}
                                            isSearchable={false}
                                            onChange={option => setSelectedFolder(option)}
                                          />
                                        </div>
                                        <div className="remove-footer">
                                          <button
                                            type="button"
                                            className="btn-cancel"
                                            disabled={loading}
                                            onClick={() => setShowAddToFolderTooltip('')}
                                          >
                                            {m({
                                              id: 'cancel',
                                              route: 'pages.dashboard'
                                            })}
                                          </button>
                                          <button
                                            type="button"
                                            disabled={loading}
                                            className="btn-primary"
                                            onClick={() =>
                                              handleUpdatePollFolder({
                                                id: poll.id,
                                                accessKey: poll.accessKey
                                              })
                                            }
                                          >
                                            {m({
                                              id: 'update',
                                              route: 'pages.dashboard'
                                            })}
                                          </button>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <div>
                                      <button
                                        type="button"
                                        // className="delete-btn"
                                        disabled={loading}
                                        onClick={() =>
                                          setShowAddToFolderTooltip(
                                            showAddToFolderTooltip === poll.id ? '' : poll.id
                                          )
                                        }
                                      >
                                        {poll.folder
                                          ? `${m({
                                              id: 'change_folder',
                                              route: 'pages.dashboard'
                                            })}`
                                          : `${m({
                                              id: 'add_to_folder',
                                              route: 'pages.dashboard'
                                            })}`}{' '}
                                        <Icon size={16} icon={ic_add} />
                                      </button>
                                    </div>
                                  </Tippy>
                                  <Tippy
                                    theme="light"
                                    // placement="left-end"
                                    // trigger="click"
                                    visible={showDeleteTooltip === poll.id}
                                    onClickOutside={() => setShowDeleteTooltip('')}
                                    interactive
                                    content={
                                      <div className="poll-remove-tooltip">
                                        <div className="tooltip-label">
                                          <Icon size={16} icon={ic_info_outline} />{' '}
                                          {m({
                                            id: 'are_you_sure',
                                            route: 'pages.dashboard'
                                          })}
                                          ?
                                        </div>
                                        <div className="tooltip-info-text">
                                          {m({
                                            id: 'deleting_your',
                                            route: 'pages.dashboard'
                                          })}{' '}
                                          "<strong>{poll.title}</strong>" poll
                                          {m({
                                            id: 'will_destroy',
                                            route: 'pages.dashboard'
                                          })}
                                          . <br />
                                          {m({
                                            id: 'cannot_undone',
                                            route: 'pages.dashboard'
                                          })}
                                          .
                                        </div>
                                        <div className="remove-footer">
                                          <button
                                            type="button"
                                            disabled={loading}
                                            className="btn-remove"
                                            onClick={() =>
                                              handleDeletePoll({
                                                id: poll.id,
                                                accessKey: poll.accessKey
                                              })
                                            }
                                          >
                                            {m({
                                              id: 'remove',
                                              route: 'pages.dashboard'
                                            })}
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-cancel"
                                            disabled={loading}
                                            onClick={() => setShowDeleteTooltip('')}
                                          >
                                            {m({
                                              id: 'cancel',
                                              route: 'pages.dashboard'
                                            })}
                                          </button>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <button
                                      type="button"
                                      className="delete-btn"
                                      disabled={loading}
                                      onClick={() =>
                                        setShowDeleteTooltip(
                                          showDeleteTooltip === poll.id ? '' : poll.id
                                        )
                                      }
                                    >
                                      {m({
                                        id: 'delete',
                                        route: 'pages.dashboard'
                                      })}{' '}
                                      <Icon size={16} icon={trashO} />
                                    </button>
                                  </Tippy>
                                </div>
                              }
                            >
                              <button type="button">
                                <Icon size={22} icon={ic_more_vert} />
                              </button>
                            </Tippy>
                          </div>
                        </div>
                        <div className="stat-block last-response-sm">
                          <div className="stat-label">
                            {lastResponse
                              ? format(new Date(lastResponse), 'EEEEEE. do, MMM', {
                                  ...localeOptions
                                })
                              : `${m({
                                  id: 'never',
                                  route: 'pages.dashboard'
                                })}`}
                          </div>
                          <div className="status-info">
                            {m({
                              id: 'last_response',
                              route: 'pages.dashboard'
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {/** empty state */}
                {polls.length === 0 && (
                  <div className="dashboard-empty-wrapper">
                    <div className="empty-img-wrapper">
                      <Image
                        src="/static/images/drawings/build_drawing.png"
                        alt="Create your polls, no code required"
                        layout="fill"
                      />
                    </div>
                    {!data ? (
                      <div>{m({ id: 'loading', route: 'pages.dashboard' })}...</div>
                    ) : (
                      <div>
                        {m({ id: 'no_polls', route: 'pages.dashboard' })}. <br />
                        {m({ id: 'get_started', route: 'pages.dashboard' })}!
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DashboardPageWrapper>
      <Footer />
    </ViewWrapper>
  );
};

const DashboardSessionWrapper = props => {
  // const { session } = props;
  const [session, sessionLoading] = useSession();
  const router = useRouter();
  // console.log({ session });
  // console.log({ session, sessionLoading });

  useEffect(() => {
    const checkIfSession = async () => {
      if (!session && !sessionLoading) {
        await localLogout(); // clear local storage data
        router.replace(`/login`);
      } else {
        await setLocalSession(session);
      }
    };
    checkIfSession();
  }, [session, sessionLoading, router]);

  if (sessionLoading) {
    return (
      <ViewWrapper>
        <HeadMeta />
        <SplashLoader />
      </ViewWrapper>
    );
  }
  if (session) {
    return <Dashboard {...props} session={session} />;
  }
  return (
    <ViewWrapper>
      <HeadMeta />
      <SplashLoader />
    </ViewWrapper>
  );
};
export default DashboardSessionWrapper;
